import {
  DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { PromoCode, VoucherStatus } from './voucher.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { LocalizedString } from '../localized-string.js';
import { TimeZone } from '../../../time/timezone.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { TrueTime } from '../../../time/true-time.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { Discount } from './discount.js';
import { PromoCodeProtocol } from './promo-code-protocol.js';
import { PercentagePromoCodeProtocol } from './percentage-promo-code-protocol.js';
import { VersionRange } from '../../core/version-range.js';
import { Result } from '../../../utility/result.js';
import { ErrorLike } from '../../core/error-like.js';

export class BusinessPromoCode extends PromoCode {
  static readonly TYPE_ID: string = 'd12cd907bc2f42c8b9931bbae4920d9a';
  public static readonly SCHEMA_VERSION: number = 4;

  private _status: VoucherStatus;
  private _usedAt: dayjs.Dayjs | null;
  private _maxUses: number | null;
  private _uses: number | null;

  constructor(
    public readonly uniqueId: string,
    public readonly code: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly protocol: PromoCodeProtocol,
    public readonly descriptions: LocalizedString | null,
    public readonly startDate: dayjs.Dayjs | null,
    public readonly expirationDate: dayjs.Dayjs | null,
    public readonly components: ComponentList,
    status: VoucherStatus,
    usedAt: dayjs.Dayjs | null,
    maxUses: number | null,
    uses: number | null,
  ) {
    super();
    this._status = status;
    this._usedAt = usedAt;
    this._maxUses = maxUses;
    this._uses = uses;
  }

  get $typeId(): string {
    return BusinessPromoCode.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, BusinessPromoCode.SCHEMA_VERSION);
  }

  get status(): VoucherStatus {
    return this._status;
  }

  get usedAt(): dayjs.Dayjs | null {
    return this._usedAt;
  }

  get maxUses(): number | null {
    return this._maxUses;
  }

  get uses(): number | null {
    return this._uses;
  }

  static basic(args: {
    uniqueId: string,
    code: string,
    businessRef: BusinessModuleRef,
    protocol: PromoCodeProtocol,
    descriptions: LocalizedString | null,
    startDate: dayjs.Dayjs | null,
    expirationDate: dayjs.Dayjs | null,
    components: ComponentList | null,
  }): BusinessPromoCode {
    return new BusinessPromoCode(
        args.uniqueId,
        args.code,
        args.businessRef,
        TrueTime.now(),
        args.protocol,
        args.descriptions,
        args.startDate,
        args.expirationDate,
        args.components ?? new ComponentList(),
        VoucherStatus.Live,
        null,
        null,
        null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessPromoCode {
    return new BusinessPromoCode(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'code'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        TimeZone.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.fromData<PromoCodeProtocol>(MapUtils.get<SerializedData>(map, 'protocol')),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(map, 'descriptions')),
        TimeZone.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'startDate')),
        TimeZone.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'expirationDate')),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
        toEnumFromString(MapUtils.get<string>(map, '$status'), VoucherStatus),
        TimeZone.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'usedAt')),
        MapUtils.getOrNull<number>(map, 'maxUses'),
        MapUtils.getOrNull<number>(map, 'uses'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'code': this.code,
      'businessRef': this.businessRef.encode(),
      'creationDate': TimeZone.encodeDate(this.creationDate),
      '$status': fromEnumAsString(this.status),
      'usedAt': TimeZone.encodeDateOrNull(this.usedAt),
      'startDate': TimeZone.encodeDateOrNull(this.startDate),
      'expirationDate': TimeZone.encodeDateOrNull(this.expirationDate),
      'protocol': context.toData(this.protocol),
      'maxUses': this.maxUses,
      'uses': this.uses,
      'descriptions': context.toDataOrNull(this.descriptions),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new BusinessPromoCode(
        this.uniqueId,
        this.code,
        this.businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        this.protocol?.clone() ?? null,
        this.descriptions?.clone() ?? null,
        TimeZone.cloneDateOrNull(this.startDate),
        TimeZone.cloneDateOrNull(this.expirationDate),
        this.components.clone(),
        this.status,
        TimeZone.cloneDateOrNull(this.usedAt),
        this.maxUses,
        this.uses,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.code === this.code &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        other.usedAt === this.usedAt &&
        other.startDate === this.startDate &&
        other.expirationDate === this.expirationDate &&
        isSameData(other.protocol, this.protocol) &&
        other.maxUses === this.maxUses &&
        other.uses === this.uses &&
        isSameData(other.descriptions, this.descriptions) &&
        other.components.isSame(this.components);
  }

  updateUsage(discount: Discount, now?: dayjs.Dayjs): void {
    if(this.status !== VoucherStatus.Live || isNullOrUndefined(this._maxUses)) {
      return;
    }

    this._uses = Math.min(this._maxUses, (this._uses ?? 0) + 1);
    if(this._uses >= this._maxUses) {
      this._usedAt = now ?? TrueTime.now();
      this._status = VoucherStatus.Used;
    }
  }

  tryUpdateUsage(discount: Discount, now: dayjs.Dayjs): Result<this, ErrorLike> {
    const self = this.clone();

    if(self.status !== VoucherStatus.Live) {
      return Result.error(new Error(`Voucher status {${self.status}} is not ${VoucherStatus.Live}.`));
    }

    if(self._maxUses == null) {
      self._uses = (self._uses ?? 0) + 1;

      return Result.ok(self);
    }

    self._uses = Math.min(self._maxUses, (self._uses ?? 0) + 1);
    if(self._uses >= self._maxUses) {
      self._usedAt = now;
      self._status = VoucherStatus.Used;
    }

    return Result.ok(self);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof BusinessPromoCode;
  }

  static assertType(dm: DataModel): dm is BusinessPromoCode {
    return dm instanceof BusinessPromoCode;
  }
}

export class BusinessPromoCodeBuilder extends DataModelBuilder<BusinessPromoCode> {
  build(context: SerializationContext, map: SerializedData): BusinessPromoCode {
    return BusinessPromoCode.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessPromoCode.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessPromoCode.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added `protocol` of type `PromoCodeProtocol`.
    if(version < 2) {
      const discount = MapUtils.get<number>(map, 'discount');
      map['protocol'] = context.toData(new PercentagePromoCodeProtocol(discount));
      version = 2;
    }

    /**
     * Version 3:
     *  - parameter `expirationDate` type {@link dayjs.Dayjs | null} introduced.
     */
    if(version < 3) {
      version = 3;
    }

    /**
     * Version 4:
     *  - parameter `startDate` type {@link dayjs.Dayjs | null} introduced.
     */
    if(version < 4) version = 4;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
